import { createReducer, on } from '@ngrx/store';

import { DocumentProcessingStatus } from '@lib/medical-documents/core/models/document.interface';
import { DocumentDetailsState, MedicalDocumentsState } from '@lib/medical-documents/core/models/medical-documents-state.interface';

import * as MedicalDocumentsActions from './medical-documents.actions';

export const initialState: MedicalDocumentsState = {
  isAiSearchFeatureEnabled: false,
  isAiMedicalDataEnabled: false,
  isAiSearchChecked: false,
  aiSearchResponseMessage: '',
  isAiSearchLoading: false,
  documentsSearchTerm: '',
  documentsPageConfig: null,
  documents: [],
  aiDocuments: [],
  uploadingDocuments: [],
  currentDocument: null,
  currentAiDocument: null,
  currentDocumentPreview: null,
  isDocumentsListLoading: false,
  isCurrentDocumentLoading: false,
  isCurrentDocumentPreviewLoading: false,
  documentsError: null,
  detailsState: DocumentDetailsState.Closed,
  documentProcessingErrors: {},
};

export const medicalDocumentsReducer = createReducer(
  initialState,

  on(MedicalDocumentsActions.setAiSearchFeatureEnabled, (state, { enabled }) => ({
    ...state,
    isAiSearchFeatureEnabled: enabled,
  })),

  on(MedicalDocumentsActions.setAiMedicalDataEnabled, (state, { enabled }) => ({
    ...state,
    isAiMedicalDataEnabled: enabled,
  })),

  on(MedicalDocumentsActions.setAiSearchChecked, (state, { checked }) => ({
    ...state,
    isAiSearchChecked: checked,
  })),

  on(MedicalDocumentsActions.setDocumentsSearchTerm, (state, { documentsSearchTerm }) => ({
    ...state,
    documentsSearchTerm,
  })),

  on(MedicalDocumentsActions.setAiSearchResponseMessage, (state, { message }) => ({
    ...state,
    aiSearchResponseMessage: message,
    isAiSearchLoading: false,
  })),

  on(MedicalDocumentsActions.getDocumentsList, state => ({
    ...state,
    isDocumentsListLoading: true,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.getDocumentsListSuccess, (state, { response }) => ({
    ...state,
    documentsPageConfig: {
      page: response.page,
      pageSize: response.pageSize,
      totalCount: response.count,
      hasNextPage: response.hasNextPage,
      hasPreviousPage: response.hasPreviousPage,
    },
    documents: [...state.documents, ...response.value],
    isDocumentsListLoading: false,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.getDocumentsListFailure, (state, { error }) => ({
    ...state,
    isDocumentsListLoading: false,
    documentsError: error,
  })),

  on(MedicalDocumentsActions.addUploadingDocument, (state, { uploadingDocument }) => ({
    ...state,
    uploadingDocuments: [...state.uploadingDocuments, uploadingDocument],
    documentsError: null,
  })),

  on(MedicalDocumentsActions.uploadDocument, state => ({
    ...state,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.uploadDocumentSuccess, (state, { uploadedDocument, uploadingDocument }) => ({
    ...state,
    uploadingDocuments: state.uploadingDocuments.map(doc =>
      doc.file === uploadingDocument.file
        ? {
            ...doc,
            id: uploadedDocument.id,
            type: uploadedDocument.type,
            documentId: uploadedDocument.id,
            uploadResponse: uploadedDocument,
            processingStatus: DocumentProcessingStatus.InProgress,
            fileLink: uploadedDocument.fileLink,
            uploadedBy: uploadedDocument.uploadedBy,
            fileHash: uploadedDocument.fileHash,
            createdAt: uploadedDocument.createdAt,
            userId: uploadedDocument.userId,
          }
        : doc
    ),
    documentsError: null,
  })),

  on(MedicalDocumentsActions.uploadDocumentFailure, (state, { error, uploadingDocument }) => ({
    ...state,
    uploadingDocuments: state.uploadingDocuments.filter(doc => doc.file !== uploadingDocument.file),
    documentsError: error,
  })),

  on(MedicalDocumentsActions.setCurrentDocumentDetails, (state, document) => ({
    ...state,
    currentDocument: document.currentDocument,
    isCurrentDocumentPreviewLoading: true,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.setCurrentDocumentDetailsSuccess, (state, { previewUrl }) => ({
    ...state,
    currentDocumentPreview: previewUrl,
    isCurrentDocumentPreviewLoading: false,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.setCurrentDocumentDetailsFailure, (state, { error }) => ({
    ...state,
    currentDocumentPreview: null,
    isCurrentDocumentPreviewLoading: false,
    documentsError: error,
  })),

  on(MedicalDocumentsActions.getAiDocumentsList, state => ({
    ...state,
    isDocumentsListLoading: true,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.getAiDocumentsListSuccess, (state, { response }) => ({
    ...state,
    // aiSearchResponseMessage: response.content?.split('\n')[0] || '',
    documentsPageConfig: initialState.documentsPageConfig,
    aiDocuments: response,
    isDocumentsListLoading: false,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.getAiDocumentsListFailure, (state, { error }) => ({
    ...state,
    isDocumentsListLoading: false,
    documentsError: error,
  })),

  on(MedicalDocumentsActions.contextSearchDocuments, state => ({
    ...state,
    isAiSearchLoading: true,
    aiSearchResponseMessage: '',
    documentsError: null,
  })),

  on(MedicalDocumentsActions.setCurrentAiDocumentDetails, (state, document) => ({
    ...state,
    currentAiDocument: document.currentAiDocument,
    isCurrentDocumentPreviewLoading: true,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.setCurrentAiDocumentDetailsSuccess, (state, { previewUrl }) => ({
    ...state,
    currentDocumentPreview: previewUrl,
    isCurrentDocumentPreviewLoading: false,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.setCurrentAiDocumentDetailsFailure, (state, { error }) => ({
    ...state,
    currentDocumentPreview: null,
    isCurrentDocumentPreviewLoading: false,
    documentsError: error,
  })),

  on(MedicalDocumentsActions.clearDocumentsList, state => ({
    ...state,
    aiSearchResponseMessage: initialState.aiSearchResponseMessage,
    isAiSearchLoading: initialState.isAiSearchLoading,
    documentsPageConfig: initialState.documentsPageConfig,
    documents: initialState.documents,
    aiDocuments: initialState.aiDocuments,
    uploadingDocuments: initialState.uploadingDocuments,
    isDocumentsListLoading: initialState.isDocumentsListLoading,
    documentsError: initialState.documentsError,
  })),

  on(MedicalDocumentsActions.clearCurrentDocument, state => ({
    ...state,
    currentDocument: initialState.currentDocument,
    currentAiDocument: initialState.currentAiDocument,
    currentDocumentPreview: initialState.currentDocumentPreview,
    isCurrentDocumentLoading: initialState.isCurrentDocumentLoading,
    isCurrentDocumentPreviewLoading: initialState.isCurrentDocumentPreviewLoading,
    documentsError: initialState.documentsError,
  })),

  on(MedicalDocumentsActions.setDocumentDetailsState, (state, { state: detailsState }) => ({
    ...state,
    detailsState,
  })),

  on(MedicalDocumentsActions.openDocumentDetails, (state, { document }) => ({
    ...state,
    currentAiDocument: document,
    detailsState: DocumentDetailsState.Opened,
  })),

  on(MedicalDocumentsActions.editDocumentDetails, state => ({
    ...state,
    detailsState: DocumentDetailsState.InEdit,
  })),

  on(MedicalDocumentsActions.closeDocumentDetails, state => ({
    ...state,
    detailsState: DocumentDetailsState.Closed,
  })),

  on(MedicalDocumentsActions.updateAiDocument, (state, { updatedDocument }) => ({
    ...state,
    aiDocuments: state.aiDocuments.map(doc => (doc.id === updatedDocument.id ? updatedDocument : doc)),
    currentAiDocument: state.currentAiDocument?.id === updatedDocument.id ? updatedDocument : state.currentAiDocument,
  })),

  on(MedicalDocumentsActions.promoteUploadingToAiDocument, (state, { updatedDocument }) => {
    return {
      ...state,
      aiDocuments: [updatedDocument, ...state.aiDocuments],
      uploadingDocuments: state.uploadingDocuments.filter(doc => doc.uploadResponse?.id !== updatedDocument.id),
      currentAiDocument: state.currentAiDocument?.id === updatedDocument.id ? updatedDocument : state.currentAiDocument,
    };
  }),

  on(MedicalDocumentsActions.processDocumentAnalysis, (state, { documentId, analysisData, errors }) => {
    const uploadingDoc = state.uploadingDocuments.find(doc => doc.uploadResponse?.id === documentId);

    if (uploadingDoc && analysisData.processingStatus === DocumentProcessingStatus.Done) {
      const newAiDocument = { ...analysisData };

      return {
        ...state,
        aiDocuments: [newAiDocument, ...state.aiDocuments],
        uploadingDocuments: state.uploadingDocuments.filter(doc => doc.uploadResponse?.id !== documentId),
        currentAiDocument: state.currentAiDocument?.id === documentId ? newAiDocument : state.currentAiDocument,
        documentProcessingErrors: errors && errors.length > 0 ? { ...state.documentProcessingErrors, [documentId]: errors } : state.documentProcessingErrors,
      };
    } else {
      const updatedAiDocuments = state.aiDocuments.map(doc => (doc.id === documentId ? { ...doc, ...analysisData } : doc));

      return {
        ...state,
        aiDocuments: updatedAiDocuments,
        currentAiDocument: state.currentAiDocument?.id === documentId ? { ...state.currentAiDocument, ...analysisData } : state.currentAiDocument,
        documentProcessingErrors: errors && errors.length > 0 ? { ...state.documentProcessingErrors, [documentId]: errors } : state.documentProcessingErrors,
      };
    }
  }),

  on(MedicalDocumentsActions.setDocumentProcessingStatus, (state, { documentId, status }) => ({
    ...state,
    aiDocuments: state.aiDocuments.map(doc => (doc.id === documentId ? { ...doc, processingStatus: status } : doc)),
    uploadingDocuments: state.uploadingDocuments.map(doc => (doc.uploadResponse?.id === documentId ? { ...doc, processingStatus: status } : doc)),
    currentAiDocument: state.currentAiDocument?.id === documentId ? { ...state.currentAiDocument, processingStatus: status } : state.currentAiDocument,
  })),

  on(MedicalDocumentsActions.removeUploadingDocument, (state, { uploadingDocument }) => ({
    ...state,
    uploadingDocuments: state.uploadingDocuments.filter(doc => doc.file !== uploadingDocument.file),
    documentsError: null,
  })),

  on(MedicalDocumentsActions.deleteAiDocument, state => ({
    ...state,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.deleteAiDocumentSuccess, (state, { documentId }) => ({
    ...state,
    aiDocuments: state.aiDocuments.filter(doc => doc.id !== documentId),
    currentAiDocument: state.currentAiDocument?.id === documentId ? null : state.currentAiDocument,
    currentDocumentPreview: state.currentAiDocument?.id === documentId ? null : state.currentDocumentPreview,
    documentsError: null,
  })),

  on(MedicalDocumentsActions.deleteAiDocumentFailure, (state, { error }) => ({
    ...state,
    documentsError: error,
  }))
);
